import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import { User, Mail, Shield, Building2, Calendar, Key, Save, Eye, EyeOff } from 'lucide-react'
import toast from 'react-hot-toast'
import sasUsersAPI from '../services/sasUsersAPI'
import { authAPI } from '../services/authAPI'
import { mfaAPI } from '../services/mfaAPI'
import MFASetup from '../components/MFA/MFASetup'

export default function Profile() {
  const { user, updateUser } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || ''
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [mfaStatus, setMfaStatus] = useState(null)
  const [showMFASetup, setShowMFASetup] = useState(false)
  const [loadingMFA, setLoadingMFA] = useState(false)

  useEffect(() => {
    setFormData({
      full_name: user?.full_name || '',
      email: user?.email || ''
    })
    fetchMFAStatus()
  }, [user])

  const fetchMFAStatus = async () => {
    try {
      const response = await mfaAPI.getStatus()
      if (response.data.success) {
        setMfaStatus(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching MFA status:', error)
    }
  }

  const handleDisableMFA = async () => {
    if (!confirm('Are you sure you want to disable MFA? This will make your account less secure.')) {
      return
    }

    setLoadingMFA(true)
    try {
      const response = await mfaAPI.disable()
      if (response.data.success) {
        toast.success('MFA disabled successfully')
        await fetchMFAStatus()
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to disable MFA')
    } finally {
      setLoadingMFA(false)
    }
  }

  const handleMFASetupComplete = () => {
    setShowMFASetup(false)
    fetchMFAStatus()
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSave = async () => {
    if (!user?.id) {
      toast.error('User not found')
      return
    }

    setIsSaving(true)
    try {
      const response = await sasUsersAPI.update(user.id, formData)
      if (response.success) {
        // Update local user state with new data
        updateUser({ ...user, ...formData, ...response.data.user })
        setIsEditing(false)
        toast.success('Profile updated successfully!')
      } else {
        toast.error(response.message || 'Failed to update profile')
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error(error.response?.data?.message || 'Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePasswordSave = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match')
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }

    setIsSaving(true)
    try {
      const response = await authAPI.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      )
      if (response.data.success) {
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        setIsChangingPassword(false)
        // Refresh user data to get updated password_changed_at
        const userResponse = await authAPI.getCurrentUser()
        if (userResponse.data.success) {
          updateUser(userResponse.data.data.user)
        }
        toast.success('Password changed successfully!')
      } else {
        toast.error(response.data.message || 'Failed to change password')
      }
    } catch (error) {
      console.error('Error changing password:', error)
      toast.error(error.response?.data?.message || 'Failed to change password')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      full_name: user?.full_name || '',
      email: user?.email || ''
    })
    setIsEditing(false)
  }

  const getRoleBadge = (role, userType) => {
    const roleColors = {
      'Super Admin': 'bg-purple-100 text-purple-800',
      'Admin': 'bg-blue-100 text-blue-800',
      'Manager': 'bg-green-100 text-green-800',
      'Organization Admin': 'bg-orange-100 text-orange-800',
      'Organization Manager': 'bg-yellow-100 text-yellow-800',
      'Organization User': 'bg-gray-100 text-gray-800'
    }
    
    const color = roleColors[role] || 'bg-gray-100 text-gray-800'
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {role}
      </span>
    )
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' }
    }
    
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const formatTimeAgo = (date) => {
    if (!date) return 'Never'
    
    const now = new Date()
    const diff = now - date
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    const weeks = Math.floor(days / 7)
    const months = Math.floor(days / 30)
    const years = Math.floor(days / 365)

    if (seconds < 60) return 'Just now'
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`
    if (weeks < 4) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`
    return `${years} year${years !== 1 ? 's' : ''} ago`
  }

  if (showMFASetup) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Setup Multi-Factor Authentication</h1>
          <p className="text-secondary-600 mt-1">Secure your account with an additional layer of protection</p>
        </div>
        <MFASetup 
          onComplete={handleMFASetupComplete}
          onCancel={() => setShowMFASetup(false)}
        />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Profile Settings</h1>
        <p className="text-secondary-600 mt-1">Manage your account information and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Overview */}
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="h-24 w-24 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <User className="h-12 w-12 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                  {user?.full_name || user?.email?.split('@')[0] || 'User'}
                </h3>
                <p className="text-sm text-secondary-600 mb-4">
                  {user?.email}
                </p>
                {getRoleBadge(user?.role, user?.user_type)}
                <div className="mt-4">
                  {getStatusBadge(user?.status)}
                </div>
              </div>
            </CardContent>
          </Card>

          
        </div>

        {/* Profile Details */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Profile Details</CardTitle>
                {!isEditing ? (
                  <Button onClick={() => setIsEditing(true)}>
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex space-x-2">
                    <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                      <Save className="h-4 w-4 mr-2" />
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-secondary-700 mb-2">
                    Full Name
                  </label>
                  {isEditing ? (
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <p className="text-secondary-900">{user?.full_name || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-2">
                    Email Address
                  </label>
                  {isEditing ? (
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                    />
                  ) : (
                    <p className="text-secondary-900">{user?.email}</p>
                  )}
                </div>

              </div>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Key className="h-5 w-5 mr-2" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!isChangingPassword ? (
                  <div className="flex items-center justify-between p-4 border border-secondary-200 rounded-lg">
                    <div>
                      <h4 className="font-medium text-secondary-900">Change Password</h4>
                      <p className="text-sm text-secondary-600">Update your password for better security</p>
                    </div>
                    <Button variant="outline" onClick={() => setIsChangingPassword(true)}>
                      Change Password
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 border border-secondary-200 rounded-lg space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Current Password *
                      </label>
                      <div className="relative">
                        <Input
                          type={showPasswords.current ? 'text' : 'password'}
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          placeholder="Enter current password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                        >
                          {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        New Password *
                      </label>
                      <div className="relative">
                        <Input
                          type={showPasswords.new ? 'text' : 'password'}
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                        >
                          {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-2">
                        Confirm New Password *
                      </label>
                      <div className="relative">
                        <Input
                          type={showPasswords.confirm ? 'text' : 'password'}
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                        >
                          {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => {
                        setIsChangingPassword(false)
                        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
                      }}>
                        Cancel
                      </Button>
                      <Button onClick={handlePasswordSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Password'}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 border border-secondary-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-secondary-900">Multi-Factor Authentication</h4>
                    <p className="text-sm text-secondary-600">
                      {mfaStatus?.is_enabled 
                        ? `Enabled - ${mfaStatus.backup_codes_count || 0} backup codes available`
                        : 'Add an extra layer of security to your account'}
                    </p>
                  </div>
                  {mfaStatus?.is_enabled ? (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={handleDisableMFA}
                        disabled={loadingMFA}
                      >
                        {loadingMFA ? 'Disabling...' : 'Disable MFA'}
                      </Button>
                      {mfaStatus.backup_codes_count < 5 && (
                        <Button 
                          variant="outline"
                          onClick={async () => {
                            try {
                              const response = await mfaAPI.regenerateBackupCodes()
                              if (response.data.success) {
                                toast.success('Backup codes regenerated! Please save them.')
                                await fetchMFAStatus()
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.message || 'Failed to regenerate backup codes')
                            }
                          }}
                        >
                          Regenerate Codes
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setShowMFASetup(true)}>
                      Enable MFA
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {user?.last_login && (
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-secondary-50">
                    <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary-900">Logged in</p>
                      <p className="text-xs text-secondary-600">
                        {formatTimeAgo(new Date(user.last_login))}
                      </p>
                    </div>
                  </div>
                )}
                
                {user?.profile_updated_at && (
                  <div className="flex items-center space-x-3 p-3 rounded-lg">
                    <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary-900">Profile updated</p>
                      <p className="text-xs text-secondary-600">
                        {formatTimeAgo(new Date(user.profile_updated_at))}
                      </p>
                    </div>
                  </div>
                )}
                
                {user?.password_changed_at && (
                  <div className="flex items-center space-x-3 p-3 rounded-lg">
                    <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary-900">Password changed</p>
                      <p className="text-xs text-secondary-600">
                        {formatTimeAgo(new Date(user.password_changed_at))}
                      </p>
                    </div>
                  </div>
                )}
                
                {!user?.last_login && !user?.profile_updated_at && !user?.password_changed_at && (
                  <p className="text-sm text-secondary-600 text-center py-4">No recent activity</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
